import { accessToken, supabase } from '@/lib/supabase';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * An error carrying the API's message, so screens can show what actually went
 * wrong rather than "Failed to fetch".
 */
export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the caller lacks the role, as opposed to being signed out. */
  get isForbidden() {
    return this.status === 403;
  }
}

async function request(method, path, { body, query, signal } = {}) {
  const token = await accessToken();

  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  }

  const response = await fetch(url, {
    method,
    signal,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return null;

  const text = await response.text();
  const payload = text ? safeParse(text) : null;

  if (!response.ok) {
    // An expired session should land the operator on the sign-in screen rather
    // than showing a confusing failure on every panel of the page.
    if (response.status === 401) {
      await supabase.auth.signOut();
    }
    const error = payload?.error ?? {};
    throw new ApiError(
      response.status,
      error.code ?? 'unknown',
      error.message ?? `Request failed (${response.status})`,
      error.details
    );
  }

  return payload;
}

function safeParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * A resource, exposing the same method names the Base44 SDK used.
 *
 * Keeping `list` / `filter` / `create` / `update` / `delete` means the screens
 * that were written against Base44 read the same way here, and the difference
 * is confined to this file.
 */
function resource(path) {
  return {
    /** @param {string} [order] `-created_at` for descending, Base44's convention. */
    list: (order, limit, offset) => request('GET', path, { query: { order, limit, offset } }),

    get: (id) => request('GET', `${path}/${id}`),

    /** Equality matching, as Base44's `.filter({...})` supported. */
    filter: (where, order, limit, offset) =>
      request('POST', `${path}/query`, { body: { where, order, limit, offset } }),

    create: (data) => request('POST', path, { body: data }),

    update: (id, data) => request('PATCH', `${path}/${id}`, { body: data }),

    /**
     * @param {string} id
     * @param {Record<string, string|boolean|undefined>} [query]
     *   Extra flags the endpoint understands - deleting an order takes
     *   `with_shipments` to refund and remove what is attached to it first.
     */
    delete: (id, query) => request('DELETE', `${path}/${id}`, { query }),
  };
}

/**
 * Upload a file.
 *
 * Returns the stored object path, which is what goes on the record, plus a
 * signed URL for showing it right now. Base44's UploadFile handed back a
 * permanent public URL that was then saved — so every uploaded 3602 and every
 * generated postage statement stayed readable by anyone who had the link.
 *
 * @param {'company-template'|'signature'|'mailing-template'|'document'} kind
 */
async function upload(kind, file, { mailingId } = {}) {
  const token = await accessToken();

  const form = new FormData();
  form.append('file', file, file.name);

  const url = new URL(`${BASE_URL}/api/uploads/${kind}`, window.location.origin);
  if (mailingId) url.searchParams.set('mailing_id', mailingId);

  const response = await fetch(url, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    // Content-Type is set by the browser, boundary included.
    body: form,
  });

  const text = await response.text();
  const payload = text ? safeParse(text) : null;

  if (!response.ok) {
    if (response.status === 401) await supabase.auth.signOut();
    const error = payload?.error ?? {};
    throw new ApiError(
      response.status,
      error.code ?? 'unknown',
      error.message ?? `Upload failed (${response.status})`,
      error.details
    );
  }

  return payload;
}

export const api = {
  raw: request,
  upload,

  /** A fresh signed URL for a stored object path. */
  /**
   * A signed URL for a stored object path.
   *
   * `download` asks storage to serve it as an attachment under that name -
   * necessary because the `download` attribute on an anchor is ignored when
   * the href points at another origin, which every stored file does.
   */
  /**
   * Which build the server is running.
   *
   * Read from the server rather than compiled into the bundle, so it reports
   * what is actually deployed. A version baked into the page would describe
   * the JavaScript being executed, which after a deploy is whatever the
   * browser still has cached - exactly when the question matters most.
   */
  health: () => request('GET', '/api/health'),

  fileUrl: (path, { download } = {}) =>
    request('GET', '/api/uploads/url', { query: { path, download } }),

  mailingDocuments: {
    list: (mailingId) => request('GET', `/api/mailings/${mailingId}/documents`),
    attach: (mailingId, body) => request('POST', `/api/mailings/${mailingId}/documents`, { body }),
    remove: (mailingId, docId) =>
      request('DELETE', `/api/mailings/${mailingId}/documents/${docId}`),
  },

  activity: {
    list: (query) => request('GET', '/api/activity-log', { query }),
    record: (body) => request('POST', '/api/activity-log', { body }),
  },

  entities: {
    Mailing: resource('/api/mailings'),
    Shipment: resource('/api/shipments'),
    BoxSize: resource('/api/box-sizes'),
    PaperStock: resource('/api/paper-stocks'),
    // Base44 called this CustomPostOffice. It is the real post office table now,
    // and the only source of destination addresses.
    PostOffice: resource('/api/post-offices'),
    PostageStatementAddress: resource('/api/postage-statement-addresses'),
    ActivityLog: resource('/api/activity-log'),
  },

  settings: {
    get: () => request('GET', '/api/settings'),
    update: (data) => request('PATCH', '/api/settings', { body: data }),
    sendTestEmail: () => request('POST', '/api/settings/email/test', { body: {} }),

    /**
     * Credential status — never values.
     *
     * There is deliberately no getSecret(): the API has no route that returns
     * a stored key, so there is nothing here to call.
     */
    secrets: () => request('GET', '/api/settings/secrets'),
    setSecret: (name, value) =>
      request('PUT', `/api/settings/secrets/${name}`, { body: { value } }),
    clearSecret: (name) => request('DELETE', `/api/settings/secrets/${name}`),
  },

  me: () => request('GET', '/api/me'),
  /** Unauthenticated: for someone who cannot sign in. */
  forgotPassword: (email) => request('POST', '/api/auth/forgot-password', { body: { email } }),
  resetPassword: (data) => request('POST', '/api/auth/reset-password', { body: data }),
  updateMe: (data) => request('PATCH', '/api/me', { body: data }),

  users: {
    list: () => request('GET', '/api/users'),
    create: (data) => request('POST', '/api/users', { body: data }),
    sendInvitation: (id) => request('POST', `/api/users/${id}/invitation`, { body: {} }),
    update: (id, data) => request('PATCH', `/api/users/${id}`, { body: data }),
    remove: (id) => request('DELETE', `/api/users/${id}`),
  },

  mailings: {
    routes: (mailingId) => request('GET', `/api/mailings/${mailingId}/routes`),
    setPostageAddress: (mailingId, address) =>
      request('PUT', `/api/mailings/${mailingId}/postage-address`, { body: { address } }),
    /** One mailing with its routes, documents and permit block. */
    full: (id) => request('GET', `/api/mailings/${id}/full`),
    /** Pack a set of routes before the mailing exists. Stores nothing. */
    packPreview: (body) => request('POST', '/api/packing/preview', { body }),
    /** Create or replace a mailing together with its routes, in one call. */
    saveFromExtraction: (body) => request('POST', '/api/mailings/from-extraction', { body }),
    pack: (mailingId, body) => request('POST', `/api/mailings/${mailingId}/packing`, { body }),
  },

  stats: () => request('GET', '/api/stats'),

  /**
   * PS Form 3602 extraction and post office resolution.
   *
   * The model call happens on the server, so no key reaches the browser. The
   * response is a *proposal*: extracted routes, a reconciliation of those
   * routes against the total the form itself declares, and a post office
   * resolution per ZIP. Nothing is saved until the operator confirms it.
   */
  extraction: {
    /** Whether the optional integrations are configured on this server. */
    status: () => request('GET', '/api/extraction/status'),

    /**
     * Read a 3602. Returns a proposal, never a saved mailing.
     *
     * Check `blocking.reconciliation` and `blocking.unconfirmed_zips` before
     * offering to save: an unbalanced form means the extracted piece counts
     * disagree with what the form declares, and those counts end up on a USPS
     * postage statement.
     */
    parse3602: async (file) => {
      const token = await accessToken();
      const form = new FormData();
      form.append('file', file, file.name);

      const response = await fetch(
        new URL(`${BASE_URL}/api/extraction/3602`, window.location.origin),
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: form,
        }
      );

      const text = await response.text();
      const payload = text ? safeParse(text) : null;

      if (!response.ok) {
        if (response.status === 401) await supabase.auth.signOut();
        const error = payload?.error ?? {};
        throw new ApiError(
          response.status,
          error.code ?? 'unknown',
          error.message ?? `Extraction failed (${response.status})`,
          error.details
        );
      }

      return payload;
    },

    /** What post office serves this ZIP, and how confident we are. */
    resolveZip: (zip) => request('GET', `/api/post-offices/resolve/${zip}`),

    /**
     * Accept an address for a ZIP.
     *
     * This is the moment an address becomes trusted: it is stored, marked
     * verified against the confirming user, and mapped to the ZIP. Every later
     * mailing to that ZIP reuses it without a lookup — and correcting this one
     * row corrects all of them.
     */
    confirmZip: (zip, address) =>
      request('POST', `/api/post-offices/resolve/${zip}/confirm`, { body: address }),

    searchAddresses: (query) => request('GET', '/api/addresses/search', { query: { q: query } }),
  },

  postOffices: {
    verify: (id) => request('POST', `/api/post-offices/${id}/verify`),
    unverify: (id) => request('POST', `/api/post-offices/${id}/unverify`),
    zipAssignments: () => request('GET', '/api/zip-assignments'),
    assignZip: (zip, postOfficeId) =>
      request('PUT', `/api/zip-assignments/${zip}`, { body: { post_office_id: postOfficeId } }),
    unassignZip: (zip) => request('DELETE', `/api/zip-assignments/${zip}`),
  },

  documents: {
    /** @param {'pvds'|'pvds-all'|'facing-slips'|'box-labels'|'acceptance'|'postage-statement'} kind */
    generate: (mailingId, kind, body) =>
      request('POST', `/api/mailings/${mailingId}/documents/${kind}`, { body }),

    /** Signed URLs expire, so ask for a fresh one at the moment of viewing. */
    url: (mailingId, kind) => request('GET', `/api/mailings/${mailingId}/documents/${kind}/url`),
  },

  shipping: {
    rates: (mailingId, boxWeights) =>
      request('POST', `/api/mailings/${mailingId}/shipping/rates`, {
        body: { box_weights: boxWeights },
      }),

    /**
     * Buy labels.
     *
     * `idempotencyKey` must be generated once per purchase attempt and reused
     * across retries — that is what stops a timeout turning into a second set
     * of labels. Callers should hold it in state, not regenerate on re-render.
     */
    purchase: (mailingId, { idempotencyKey, boxWeights, selections }) =>
      request('POST', `/api/mailings/${mailingId}/shipping/purchase`, {
        body: {
          idempotency_key: idempotencyKey,
          box_weights: boxWeights,
          selections,
        },
      }),

    handDelivery: (mailingId) =>
      request('POST', `/api/mailings/${mailingId}/shipping/hand-delivery`),

    freight: (mailingId, body) =>
      request('POST', `/api/mailings/${mailingId}/shipping/freight`, { body }),

    labelUrl: (shipmentId) => request('GET', `/api/shipments/${shipmentId}/label-url`),

    printAll: (mailingId) => request('POST', `/api/mailings/${mailingId}/labels/print`),

    /** Price a replacement label for a box that was refunded. */
    reshipRates: (shipmentId, weight) =>
      request('POST', `/api/shipments/${shipmentId}/reship/rates`, { body: { weight } }),

    /** Buy the replacement. Creates a new record; the original is left intact. */
    reship: (shipmentId, body) => request('POST', `/api/shipments/${shipmentId}/reship`, { body }),

    /** Admin only. */
    refund: (shipmentId) => request('POST', `/api/shipments/${shipmentId}/refund`),

    /** Admin only. Soft-deletes, keeping the Shippo transaction id. */
    /**
     * Remove a shipment. A purchased label is always refunded first; `force`
     * removes it anyway when that refund cannot be made.
     */
    remove: (shipmentId, { force = false } = {}) =>
      request('DELETE', `/api/shipments/${shipmentId}`, {
        query: { force: force ? 'true' : undefined },
      }),
  },
};

/** A fresh idempotency key for one purchase attempt. */
export function newIdempotencyKey() {
  return crypto.randomUUID();
}
